import { OrderShippingMethod } from '@codezo/graphql';
import { type DeliveryDraft, filled, isDeliveryOffered } from './delivery-rules';

/**
 * When a split is worth saving, and when it isn't.
 *
 * A split is a third option on one product's quantity tier: part of the tier
 * goes by one method, the rest by another — "20 Express, 80 Standard". It sits
 * next to Standard and Express and the client picks it exactly like either.
 *
 * The backend enforces every rule here and returns a message when one is
 * broken. These are the same rules stated early, so the admin sees the problem
 * while typing rather than on a rejected save; the server's message is still
 * what gets surfaced when a write is refused.
 *
 * ── A leg carries its own lead time ──
 *
 * Not its method's. These briefly inherited from the method card above, on the
 * reasoning that an Express leg travels at Express speed — but a split's legs
 * are not the tier's methods. A tier may offer only Standard on its own and
 * still carry a split with an Express half, and then there is no method card to
 * inherit from. Separately, the Standard half of a split is a different
 * consignment from a whole-tier Standard shipment and a forwarder may quote it
 * differently.
 *
 * So a leg is priced and timed like a method in its own right: `SplitLegDraft`
 * extends `DeliveryDraft` and is judged by the same `isDeliveryOffered`.
 *
 * ── The last leg's quantity is NOT entered ──
 *
 * It is the remainder, derived. A split decides how the tier divides, never how
 * much is bought, so the one number that can't be wrong is the one nobody
 * types: the legs always add up because the last is defined as what the others
 * left.
 */

/**
 * One leg being entered.
 *
 * `key` is local and has no server meaning. Legs are replaced wholesale on every
 * save and their identity server-side is their position, so a React key is all
 * an id would be; it exists so deleting a leg doesn't make the inputs below it
 * inherit the wrong values.
 */
export interface SplitLegDraft extends DeliveryDraft {
	key: string;
	method: OrderShippingMethod;
	/** Ignored on the last leg, which takes whatever the others leave. */
	quantity: string;
}

export interface SplitDraft {
	/** Delivery id when the split already exists server-side; null when it doesn't. */
	existingId: string | null;
	/**
	 * False when a re-quantified tier left a saved split's legs no longer adding
	 * up. Carried from the server so the editor can say the split needs redoing
	 * before anything is touched — a freshly assembled draft can't reach that
	 * state, since its last leg absorbs the difference by construction.
	 */
	serverValid: boolean;
	legs: SplitLegDraft[];
}

let legSeq = 0;
const nextKey = () => `leg-${++legSeq}`;

export const emptyLeg = (method = OrderShippingMethod.Standard): SplitLegDraft => ({
	key: nextKey(),
	method,
	quantity: '',
	price: '',
	deliveryDaysMin: '',
	deliveryDaysMax: '',
});

/**
 * A new split starts as the two methods it exists to combine, in the order they
 * travel: the fast part goes first, because sending part of the order ahead is
 * the whole reason for the option.
 */
export const emptySplit = (): SplitDraft => ({
	existingId: null,
	serverValid: true,
	legs: [emptyLeg(OrderShippingMethod.Express), emptyLeg(OrderShippingMethod.Standard)],
});

const entered = (leg: SplitLegDraft) => {
	const n = parseInt(leg.quantity, 10);
	return Number.isFinite(n) ? n : 0;
};

/**
 * What each leg carries, the last one being the remainder.
 *
 * Always as long as `legs`, and always sums to exactly `tierQuantity` — except
 * when the entered legs overshoot it, where the last comes back negative rather
 * than clamped to zero. Clamping would make an impossible split look merely
 * empty; negative is the state, and it is what the UI reports.
 */
export const legQuantities = (split: SplitDraft, tierQuantity: number): number[] => {
	const lead = split.legs.slice(0, -1).map(entered);
	const used = lead.reduce((sum, n) => sum + n, 0);
	return [...lead, tierQuantity - used];
};

/** What the last leg is left holding. Negative when the others overshoot. */
export const remainder = (split: SplitDraft, tierQuantity: number) => {
	const quantities = legQuantities(split, tierQuantity);
	return quantities[quantities.length - 1] ?? 0;
};

/** Nothing entered on any leg — an untouched split, safe to drop silently. */
export const isSplitBlank = (split: SplitDraft) =>
	split.legs.every(
		(leg) =>
			!filled(leg.quantity) &&
			!filled(leg.price) &&
			!filled(leg.deliveryDaysMin) &&
			!filled(leg.deliveryDaysMax)
	);

/**
 * Writable. Every rule the backend enforces:
 *
 * - at least two legs — one leg is a plain option, not a split
 * - each leg priced
 * - each leg a whole quantity above zero, the last one included
 *
 * The "legs add up to the tier" rule is not checked because it cannot be
 * broken: the last leg IS the difference. What can go wrong is the others
 * claiming more than the tier holds, which shows up as a non-positive
 * remainder.
 */
export const isSplitWritable = (split: SplitDraft, tierQuantity: number) => {
	if (split.legs.length < 2) return false;
	/* The same bar a plain method clears: priced, with days both set or both
	   open. A split can be priced before the forwarder answers, exactly as a
	   method can — it just can't be sent to a client until they are in. */
	if (!split.legs.every(isDeliveryOffered)) return false;
	return legQuantities(split, tierQuantity).every((n) => n > 0);
};

/**
 * Why a started split can't be written yet — one reason at a time, in the order
 * an admin would fix them, so the card never reads as a list of complaints.
 *
 * Null once it is writable, and null when it is blank: an untouched split is
 * not a problem to solve, it is something to fill in or leave.
 */
export const splitIncompleteReason = (split: SplitDraft, tierQuantity: number): string | null => {
	if (isSplitBlank(split) || isSplitWritable(split, tierQuantity)) return null;
	if (split.legs.length < 2) {
		return 'A split needs at least two parts — one part is just a plain method.';
	}
	const quantities = legQuantities(split, tierQuantity);
	const left = quantities[quantities.length - 1] ?? 0;
	if (left < 0) {
		return `${Math.abs(left).toLocaleString()} pcs too many — the parts can't claim more than the ${tierQuantity.toLocaleString()} this tier buys.`;
	}
	if (left === 0) {
		return 'The last part is left with nothing — take some off the parts above it.';
	}
	if (quantities.some((n) => n <= 0)) {
		return 'Every part needs a quantity above zero.';
	}
	if (split.legs.some((leg) => !filled(leg.price))) {
		return 'Every part needs its own shipping price.';
	}
	return 'Set both transit days on a part, or clear both to leave its lead time open.';
};

/**
 * Started but not writable, and so holding up the save. A blank split is not
 * incomplete — it is simply not a split yet, and is dropped rather than saved.
 */
export const isSplitIncomplete = (split: SplitDraft, tierQuantity: number) =>
	!isSplitBlank(split) && !isSplitWritable(split, tierQuantity);

/**
 * The split said as a sentence — "20 Express first, 80 Standard after".
 *
 * The card's own heading. A split has no name, and "Split" alone makes two of
 * them on one tier indistinguishable; the order matters as much as the amounts,
 * which is why this reads sequentially rather than as "20 Express + 80
 * Standard".
 */
export const splitSummary = (
	split: SplitDraft,
	tierQuantity: number,
	label: (method: OrderShippingMethod) => string
): string => {
	const quantities = legQuantities(split, tierQuantity);
	return split.legs
		.map((leg, i) => {
			const n = quantities[i];
			const when = i === 0 ? 'first' : i === split.legs.length - 1 ? 'after' : 'next';
			return `${n > 0 ? n.toLocaleString() : '—'} ${label(leg.method)} ${when}`;
		})
		.join(', ');
};
